var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { horizontal_to_vertical_fov, vertical_to_horizontal_fov } from '../Math.js';
import { IMatrix4, CameraMode } from '../api.js';
import { Deprecated } from '../utils/Deprecated.js';
export var FieldOfViewDirection;
(function (FieldOfViewDirection) {
    FieldOfViewDirection[FieldOfViewDirection["HORIZONTAL"] = 0] = "HORIZONTAL";
    FieldOfViewDirection[FieldOfViewDirection["VERTICAL"] = 1] = "VERTICAL";
})(FieldOfViewDirection || (FieldOfViewDirection = {}));
export class PerspectiveProjection {
    constructor(field_of_view, aspect_ratio, near_z, far_z, field_of_view_direction = FieldOfViewDirection.HORIZONTAL) {
        this._projection_matrix = PerspectiveProjection.calculate_perspective(field_of_view, aspect_ratio, near_z, far_z, field_of_view_direction);
        this._is_dirty = false;
        this._field_of_view = field_of_view;
        this._field_of_view_direction = field_of_view_direction;
        this._aspect_ratio = aspect_ratio;
        this._near = near_z;
        this._far = far_z;
    }
    get_camera_mode() {
        return CameraMode.PERSPECTIVE;
    }
    get_aspect_ratio() {
        return this._aspect_ratio;
    }
    get_projection_matrix() {
        if (this.is_dirty) {
            this.is_dirty = false;
            // Make sure matrix is up to date
            this._projection_matrix = PerspectiveProjection.calculate_perspective(this._field_of_view, this._aspect_ratio, this._near, this._far, this._field_of_view_direction);
        }
        return this._projection_matrix;
    }
    get is_dirty() {
        return this._is_dirty;
    }
    set is_dirty(dirty) {
        this._is_dirty = dirty;
    }
    get vertical_fov() {
        if (this._field_of_view_direction === FieldOfViewDirection.HORIZONTAL) {
            return horizontal_to_vertical_fov(this._field_of_view, this._aspect_ratio);
        }
        return this._field_of_view;
    }
    get horizontal_fov() {
        if (this._field_of_view_direction === FieldOfViewDirection.VERTICAL) {
            return vertical_to_horizontal_fov(this._field_of_view, this._aspect_ratio);
        }
        return this._field_of_view;
    }
    /**
     * @deprecated Use field_of_view instead
     **/
    set horizontal_fov(val) {
        if (this._field_of_view_direction === FieldOfViewDirection.VERTICAL) {
            throw new Error("can't set horizontal_fov on a projection using vertical field of view");
        }
        this.field_of_view = val;
    }
    get field_of_view() {
        return this._field_of_view;
    }
    set field_of_view(val) {
        if (val === this._field_of_view)
            return;
        this._field_of_view = val;
        this.is_dirty = true;
    }
    get field_of_view_direction() {
        return this._field_of_view_direction;
    }
    set field_of_view_direction(val) {
        if (val === this._field_of_view_direction)
            return;
        this._field_of_view_direction = val;
        this.is_dirty = true;
    }
    get aspect_ratio() {
        return this._aspect_ratio;
    }
    set aspect_ratio(val) {
        if (val === this._aspect_ratio)
            return;
        this._aspect_ratio = val;
        this.is_dirty = true;
    }
    get near() {
        return this._near;
    }
    set near(val) {
        val = Math.max(val, PerspectiveProjection.ALLOWED_PLANE.near);
        if (val === this._near)
            return;
        this._near = val;
        this.is_dirty = true;
    }
    get far() {
        return this._far;
    }
    set far(val) {
        val = Math.min(val, PerspectiveProjection.ALLOWED_PLANE.far);
        if (val === this._far)
            return;
        this._far = val;
        this.is_dirty = true;
    }
    clone() {
        return new PerspectiveProjection(this._field_of_view, this._aspect_ratio, this._near, this._far, this._field_of_view_direction);
    }
    /**
     * Calculate a right-handed perspective projection
     * @param field_of_view the field of view
     * @param aspect_ratio the aspect ratio
     * @param near_z the near clip plane
     * @param far_z the far clip plane
     * @param field_of_view_direction the direction of the field of view
     * @returns perspective projection matrix
     */
    static calculate_perspective(field_of_view, aspect_ratio, near_z, far_z, field_of_view_direction) {
        // TODO Consider Left handed system c *= -1.0; d *= -1.0;
        const t = 1.0 / Math.tan(field_of_view * 0.5);
        const a = field_of_view_direction === FieldOfViewDirection.HORIZONTAL ? t : t / aspect_ratio;
        const b = field_of_view_direction === FieldOfViewDirection.HORIZONTAL ? t * aspect_ratio : t;
        const c = (far_z + near_z) / (far_z - near_z);
        const d = (-2 * far_z * near_z) / (far_z - near_z);
        const projection_matrix = IMatrix4.from_values(a, 0.0, 0.0, 0.0, 0.0, b, 0.0, 0.0, 0.0, 0.0, c, 1.0, 0.0, 0.0, d, 0.0);
        return projection_matrix;
    }
}
PerspectiveProjection.ALLOWED_PLANE = { near: 0.01, far: Number.POSITIVE_INFINITY };
__decorate([
    Deprecated({ new: 'PerspectiveProjection.field_of_view' })
], PerspectiveProjection.prototype, "horizontal_fov", null);
//# sourceMappingURL=PerspectiveProjection.js.map